'use client'

import { IconInfoCircleFill } from '@macarongfactory/web-icons'
import { Divider, InfoBox, OutlineButton, Text } from '@macarongfactory/web-ui'
import { Fragment, useRef, useState } from 'react'

import { ExpandFooter, ReviewItem, type IReview } from '@/entities/place'
import { useImpressionLog } from '@/features/seoPlaceDetail/model'
import {
  type IPlaceReviewSummary,
  type IPositiveReviewRate,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import { logEvent } from '@/shared/external/firebase/analytics'
import { useSeoOnelinkUrl } from '@/shared/external/onelink'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import { cn } from '@/shared/lib/cn'

import PlaceReviewSummary from './PlaceReviewSummary'
import ReviewEmpty from './ReviewEmpty'
import { PLACE_DETAIL_REVIEW_MORE_ONELINK_URL } from '../../lib'
import PlaceSectionDivider from '../PlaceSectionDivider/PlaceSectionDivider'

const VISIBLE_BEFORE_EXPAND = 5
// 펼친 상태에서 11번째 리뷰가 존재하면(총 11개 이상) 마지막을 블러 처리하고 앱 유도 CTA 노출
const APP_CTA_THRESHOLD = 10
const APP_CTA_BUTTON_TEXT = '앱으로 편하게 보기'
// 제휴 유입은 더보기 없이 10개까지 바로 노출한다 (WEB-5901)
const PARTNER_VISIBLE_REVIEW_COUNT = 10
const PARTNER_REVIEW_INFO_TEXT =
  '최대 10개까지 확인 가능하며, 더 많은 리뷰는 마이클 앱에서 확인할 수 있습니다.'

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.REVIEW,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.REVIEW,
}
const EXPAND_BUTTON_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.REVIEW,
  type: TYPE.BUTTON,
  label: LABELS.COMMON_WEB_PLACE.EXPAND,
}
const APP_CTA_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.REVIEW,
  type: TYPE.BUTTON,
  label: LABELS.COMMON_WEB_PLACE.REVIEW_MORE_BUTTON,
}

interface IPlaceReviewSectionProps {
  place: ISeoPlace
  reviews: IReview[]
  reviewSummary: IPlaceReviewSummary | null
  positiveReviewRate: IPositiveReviewRate | null
  isPartnerEntry?: boolean
}

const PlaceReviewSection = ({
  place,
  reviews,
  reviewSummary,
  positiveReviewRate,
  isPartnerEntry,
}: IPlaceReviewSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const appOnelinkUrl = useSeoOnelinkUrl(PLACE_DETAIL_REVIEW_MORE_ONELINK_URL)
  const sectionRef = useRef<HTMLElement | null>(null)
  const expandButtonRef = useRef<HTMLDivElement | null>(null)
  const appCtaRef = useRef<HTMLDivElement | null>(null)
  const hasMore = reviews.length > VISIBLE_BEFORE_EXPAND
  // 총 11개 이상(마지막 1개는 블러 배경)일 때만 펼친 상태에서 앱 유도 CTA 노출
  // 제휴 유입이면 앱 설치 유도 경로를 모두 없앤다 (WEB-5901)
  const showAppCta =
    !isPartnerEntry && isExpanded && reviews.length > APP_CTA_THRESHOLD

  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
  })
  useImpressionLog({
    ref: expandButtonRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.ITEM.SHOW,
    params: EXPAND_BUTTON_PARAMS,
    enabled: hasMore && !isExpanded,
  })
  useImpressionLog({
    ref: appCtaRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.ITEM.SHOW,
    params: APP_CTA_PARAMS,
    enabled: showAppCta,
  })

  const handleExpandClick = () => {
    if (!isExpanded) {
      logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, EXPAND_BUTTON_PARAMS)
    }
    setIsExpanded(prev => !prev)
  }

  const handleAppCtaClick = () => {
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, APP_CTA_PARAMS)
    window.open(appOnelinkUrl, '_blank', 'noopener,noreferrer')
  }

  const isEmpty = reviews.length === 0
  // 총 리뷰 수: 앱 전체 후기 수(statistic) 우선, 없으면 로드된 리뷰 수로 폴백
  const totalReviewCount = place.statistic?.reviewCount ?? reviews.length
  const visibleReviews = isPartnerEntry
    ? reviews.slice(0, PARTNER_VISIBLE_REVIEW_COUNT)
    : reviews.slice(0, isExpanded ? reviews.length : VISIBLE_BEFORE_EXPAND)
  const lastIndex = visibleReviews.length - 1

  return (
    <>
      <PlaceSectionDivider />
      <section
        ref={sectionRef}
        id='place-review-section'
        aria-label='리뷰'
        className={cn(
          'flex flex-col gap-dimension-x4 bg-background-surface-default py-dimension-x6',
          // 제휴 유입은 고정 탑 네비게이션 높이만큼 스크롤 여백을 둬 섹션 상단이 가려지지 않게 한다 (WEB-5901)
          isPartnerEntry && 'scroll-mt-dimension-x14'
        )}
      >
        <div className='flex items-center gap-dimension-x1 px-dimension-x5'>
          <Text
            as='h2'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
          >
            리뷰
          </Text>
          {totalReviewCount > 0 && (
            <Text
              variant='title-1-semibold'
              color='text-foreground-brand-strong'
            >
              {totalReviewCount.toLocaleString()}
            </Text>
          )}
        </div>

        {isEmpty ? (
          <ReviewEmpty />
        ) : (
          <>
            <div className='px-dimension-x5'>
              <PlaceReviewSummary
                place={place}
                reviewSummary={reviewSummary}
                positiveReviewRate={positiveReviewRate}
              />
            </div>

            <div className='flex flex-col'>
              {visibleReviews.map((review, index) => {
                const isLast = showAppCta && index === lastIndex
                return (
                  <Fragment key={review.serverId}>
                    {index > 0 && <Divider.Horizontal />}
                    {isLast ? (
                      <div className='relative'>
                        {/* 마지막 리뷰를 배경으로 깔고 컨테이너 높이는 CTA가 결정 */}
                        <div className='absolute inset-0 overflow-clip'>
                          <ReviewItem
                            review={review}
                            subjectName={place.placeName}
                          />
                        </div>
                        <div
                          ref={appCtaRef}
                          className='relative z-10 flex flex-col items-center gap-dimension-x3 bg-[rgba(255,255,255,0.8)] py-[52px] backdrop-blur-[4px]'
                        >
                          <p className='whitespace-pre-line text-center text-[16px] font-semibold leading-[24px] text-foreground-neutral-strong'>
                            {`마이클 앱에서\n${totalReviewCount.toLocaleString()}개의 정비 후기를 볼 수 있어요!`}
                          </p>
                          <OutlineButton
                            text={APP_CTA_BUTTON_TEXT}
                            size='medium'
                            variant='brand'
                            clickable={handleAppCtaClick}
                          />
                        </div>
                      </div>
                    ) : (
                      <ReviewItem
                        review={review}
                        subjectName={place.placeName}
                      />
                    )}
                  </Fragment>
                )
              })}
            </div>

            {!isPartnerEntry && hasMore && (
              <div ref={expandButtonRef}>
                <ExpandFooter
                  text={isExpanded ? '닫기' : '더보기'}
                  chevron={isExpanded ? 'up' : 'down'}
                  onClick={handleExpandClick}
                />
              </div>
            )}

            {isPartnerEntry &&
              reviews.length > PARTNER_VISIBLE_REVIEW_COUNT && (
                <div className='px-dimension-x5'>
                  <InfoBox
                    text={PARTNER_REVIEW_INFO_TEXT}
                    icon={<IconInfoCircleFill width={20} height={20} />}
                  />
                </div>
              )}
          </>
        )}
      </section>
    </>
  )
}

export default PlaceReviewSection
